import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { createUseStyles } from '../../../utils/styles';
import { getOverrides, useClasses } from '../../../utils/overrides';

import { TreeContext } from './Context';
import styles from './styles';
import TreeNode from './TreeNode';

const useStyles = createUseStyles(styles, 'Tree');

const Tree = memo(
    ({
        canSelectParents,
        classes: classesProp,
        customs = {},
        id,
        nodes = {},
        onSelect,
        onToggle,
        overrides: overridesProp,
    }) => {
        // ========================================
        // CONTEXT & CUSTOM HOOKS
        // ========================================
        const classes = useClasses(useStyles, classesProp);
        const override = getOverrides(overridesProp, Tree.overrides);

        // ========================================
        // LOCAL STATE
        // ========================================
        // Track active node path for selection state
        const [activePath, setActivePath] = useState(null);
        // Track toggled paths as a Set for O(1) lookup
        const [toggledPaths, setToggledPaths] = useState(() => new Set());
        // Store the original nodes data
        const [data, setData] = useState(nodes);

        // Use refs for callbacks to avoid recreating handlers when callbacks change
        const onSelectRef = useRef(onSelect);
        const onToggleRef = useRef(onToggle);
        const canSelectParentsRef = useRef(canSelectParents);

        // Keep refs updated
        useEffect(() => {
            onSelectRef.current = onSelect;
            onToggleRef.current = onToggle;
            canSelectParentsRef.current = canSelectParents;
        }, [onSelect, onToggle, canSelectParents]);

        // ========================================
        // INITIALIZE STATE FROM DATA
        // ========================================
        const initializeToggledPaths = useCallback((node, path, paths) => {
            if (node.toggled) {
                paths.add(path);
            }
            if (node.children) {
                node.children.forEach((child, index) => {
                    initializeToggledPaths(child, `${path}-${index}`, paths);
                });
            }
        }, []);

        const findActivePath = useCallback((node, path) => {
            if (node.active) return path;
            if (node.children) {
                for (let i = 0; i < node.children.length; i++) {
                    const found = findActivePath(node.children[i], `${path}-${i}`);
                    if (found) return found;
                }
            }
            return null;
        }, []);

        // ========================================
        // EVENT HANDLERS & CALLBACKS (stable references)
        // ========================================
        const handleSelectNode = useCallback((node, path) => {
            const canSelect = canSelectParentsRef.current;
            const isLeaf = !node.children || !node.children.length;

            if ((canSelect || isLeaf) && !node.isDisabled) {
                setActivePath(path);
                onSelectRef.current?.(node);
            }
        }, []);

        const handleToggleNode = useCallback((node, path, toggled) => {
            if (node.children && !node.isDisabled) {
                setToggledPaths((prev) => {
                    const next = new Set(prev);
                    if (toggled) {
                        next.add(path);
                    } else {
                        next.delete(path);
                    }
                    return next;
                });
                onToggleRef.current?.(node, toggled);
            }
        }, []);

        // ========================================
        // EFFECTS
        // ========================================
        useEffect(() => {
            setData(nodes);
            if (nodes && nodes.name) {
                // Initialize toggled state from incoming nodes data
                const initialToggled = new Set();
                initializeToggledPaths(nodes, '0', initialToggled);
                setToggledPaths(initialToggled);
                // Initialize active state from incoming nodes data
                setActivePath(findActivePath(nodes, '0'));
            } else {
                setToggledPaths(new Set());
                setActivePath(null);
            }
        }, [nodes, initializeToggledPaths, findActivePath]);

        // ========================================
        // CONTEXT VALUE (memoized to prevent re-renders)
        // ========================================
        const contextValue = useMemo(
            () => ({
                activePath,
                canSelectParents,
                classes,
                customs,
                id,
                onSelectNode: handleSelectNode,
                onToggleNode: handleToggleNode,
                override,
                toggledPaths,
            }),
            [
                activePath,
                canSelectParents,
                classes,
                customs,
                handleSelectNode,
                handleToggleNode,
                id,
                override,
                toggledPaths,
            ],
        );

        // ========================================
        // MEMOIZED PROPS
        // ========================================
        const rootProps = useMemo(
            () => ({
                className: classes.treeRoot,
                ...override.root,
            }),
            [classes.treeRoot, override.root],
        );

        // ========================================
        // RENDER
        // ========================================
        if (!data || !data.name) {
            return null;
        }

        return (
            <TreeContext.Provider value={contextValue}>
                <ul {...rootProps}>
                    <TreeNode node={data} path="0" />
                </ul>
            </TreeContext.Provider>
        );
    },
);

Tree.displayName = 'Tree';
Tree.overrides = ['root', 'node', 'nodeItem', 'nodeIcon'];

Tree.propTypes = {
    /** When true, allows parent nodes with children to be selectable. When false, only leaf nodes can be selected */
    canSelectParents: PropTypes.bool,
    /** Custom classes applied to the component's root element and inner elements */
    classes: PropTypes.object,
    /** Custom components to override default tree rendering for advanced customization. For best performance, memoize this object with useMemo */
    customs: PropTypes.shape({
        /** Custom container component that wraps the entire node structure */
        Container: PropTypes.func,
        /** Custom node component that renders the entire node (including icon and content) */
        Node: PropTypes.func,
        /** Custom icon component for node expansion/collapse indicators */
        NodeIcon: PropTypes.func,
        /** Custom component for rendering the content/label of each node */
        NodeItem: PropTypes.func,
    }),
    /** Unique identifier for the tree, useful for custom styling and targeting specific tree instances */
    id: PropTypes.any,
    /** Tree data structure defining the nodes hierarchy. Should be a nested object with specific properties */
    nodes: PropTypes.shape({
        /** Determines if the node is currently selected */
        active: PropTypes.bool,
        /** Array of child nodes with the same structure */
        children: PropTypes.array,
        /** Unique identifier for the node, used for selection and state tracking */
        id: PropTypes.any,
        /** When true, the node cannot be selected or toggled */
        isDisabled: PropTypes.bool,
        /** Determines if the node can be selected (this is automatically determined based on node type and canSelectParents) */
        isSelectable: PropTypes.bool,
        /** Text label displayed for the node */
        name: PropTypes.string,
        /** Determines if the node is expanded (true) or collapsed (false) */
        toggled: PropTypes.bool,
    }),
    /** Function called when a node is selected. Receives the selected node object as parameter */
    onSelect: PropTypes.func,
    /** Function called when a node is toggled (expanded/collapsed). Receives the node object and toggled state as parameters */
    onToggle: PropTypes.func,
    /** Override the styles of any part of the component. Accepts overrides for 'root', 'node', 'nodeItem', and 'nodeIcon'. For best performance, memoize this object with useMemo */
    overrides: PropTypes.object,
};

export default Tree;
